import { BO, BusinessObjectBase, CollectionChangedJoinPoint, FeatureFlagsRulesServiceBase, PropertyChangedJoinPoint, QueryBus, Weaver } from '@skylab-shared/core';
import { AddressModel, AdvanceSummary, Company, DocumentPaymentListItemModel, ExchangeRateModel, SaleAdvanceDocumentItemModel, SaleAdvanceDocumentModel, SaleProFormaModel } from '@skylab-shared/dataModel';
import { Calc, MidpointRounding, dateDiffInDays, getMinimalOfDates, isUndefined, last, not, substractDays } from '@skylab-shared/utils';
import { ClientRepositoryBase } from '../client/client.repo-base';
import { ContextKeys } from '../common/context-keys';
import { propertyChangedJoinPointFilter } from '../common/property-changed/property-changed-filter';
import { CommunityTransactionForSaleQuery } from '../community-transaction/queries/community-transaction-for-sale.query-base';
import { CompanyParametersQuery } from '../company/queries/company-parameters.query-base';
import { CurrencyCollectionQuery } from '../currency/queries/currency.query-base';
import { AdvanceInvoiceDocumentGrossCalculator } from '../documents/algorithms/document/advance-invoice-document-calculator';
import { DocumentExchangeRateValidationBoPart } from '../documents/bo-parts/document-exchange-rate-validation.bo-part';
import { DocumentItemsNumberingBoPart } from '../documents/bo-parts/document-items-numbering.bo-part';
import { DocumentNumberBoPart } from '../documents/bo-parts/document-number.bo-part';
import { DocumentVatRateForSaleValidationBoPart } from '../documents/bo-parts/document-vat-rate-for-sale-validation.bo-part';
import { DocumentWstoBoPart } from '../documents/bo-parts/document-wsto.bo-part';
import { CommunityTransactionForSaleMixin } from '../documents/mixins/community-transaction-for-sale.mixin';
import { ExchangeRateMixin } from '../documents/mixins/exchange-rate.mixin';
import { DocumentLinks, DocumentLinksQuery } from '../documents/queries/document-links.query-base';
import { rewriteAddress, rewriteDocumentContractorWithFreshDataAsync } from '../documents/utils/document-client-utils';
import { NumberingQuery } from '../numbering/queries/numbering.query-base';
import { ProductRepositoryContract } from '../shared-business-temporary-contracts';
import { LoggedUserQuery } from '../user/queries/logged-user.query-base';
import { UserCollectionQuery } from '../user/queries/user.query-base';
import { BusinessDayCalculatorPL } from '../utils/business-days-calculator';
import { collectionChangedJoinPointFilter } from '../utils/collection-changed-filter';
import { VatRateForSaleQuery } from '../vat/queries/vat-rate-for-sale.query-base';
import { VatReleaseReasonQuery } from '../vat/queries/vat-release-reason.query-base';
import { BankAccountNumberMixin } from './mixins/bank-account.mixin';
import { ClientMixin } from './mixins/client.mixin';
import { SaleLinksMixin } from './mixins/sale-links.mixin';
import { TaxLiabilityDateMixin } from './mixins/tax-liability-date.mixin';
import { SaleDocumentCalculatorBoPart } from './parts/sale-document-calculator.bo-part';
import { SaleDocumentClientBoPart } from './parts/sale-document-client.bo-part';
import { SaleDocumentIssuedToInventoryGoodsIssueBoPart } from './parts/sale-document-issued-to-inventory-goods-issue.bo-part';
import { SaleDocumentItemsValidationsBoPart } from './parts/sale-document-items-validations.bo-part';
import { SaleDocumentJpkGroupsBoPart } from './parts/sale-document-jpk-groups.bo-part';
import { SaleDocumentKsefBoPart } from './parts/sale-document-ksef.bo-part';
import { SaleDocumentPaymentsBoPart } from './parts/sale-document-payment.bo-part';
import { SaleDocumentReverseChargeAndNPBoPart } from './parts/sale-document-reverse-charge-and-NP.bo-part';
import { SaleDocumentSplitPaymentBoPart } from './parts/sale-document-split-payment.bo-part';
import { SaleDocumentVatReleaseBoPart } from './parts/sale-document-vat-release.bo-part';
import { SaleDocumentWithDueDateBoPart } from './parts/sale-document-with-due-date.bo-part';
import { AdvanceSummaryQuery } from './queries/advance-summary.query-base';
import { SaleAdvancePlainObjectQuery } from './queries/sale-advance-plain-object.query-base';
import { SaleKindQuery } from './queries/sale-kind.query-base';
import { vatRateChangedOnSaleAdvance, vatRateChangedOnSaleAdvanceItem } from './utils/sale-advance-utils';
import { getDocumentCalculatorForSaleDocumentKind } from './utils/sale-document-calculators-utils';
import { rewriteAdvanceInvoiceFromProFormaAsync, rewriteAdvancePlainObjectToDocumentModel, rewriteSaleDocumentItem, rewriteVatReleaseProperties } from './utils/sale-rewriting-utils';
import { getDefaultNPValues, refreshSaleDocumentItemPriceAsync } from './utils/sale-utils';
import { AdvanceValueGreaterThanRemainingAmount } from './violations/advance-value-greater-than-remaining-amount';
import { AdvanceValueLessThanZeroError } from './violations/advance-value-less-than-zero-error';
import { AmountOfPaymentGreaterThanAdvanceValueError } from './violations/amount-of-payment-greater-than-advance-value-error';
import { DateIncludedInAccountingWarning } from './violations/date-included-in-accounting-warning';
import { IssueDateEarlierThanPreviousAdvanceError } from './violations/issue-date-earlier-than-previous-advance-error';
import { OnePaymentOnAdvanceDocumentError } from './violations/one-payment-on-advance-document-error';
import { SaleDateEarlierThanPreviousAdvanceError } from './violations/sale-date-earlier-than-previous-advance-error';
import { VatRateHasAlreadyChangedOnItemError } from './violations/vat-rate-already-changed-on-item-error';

export interface SaleAdvanceBO extends ClientMixin<SaleAdvanceDocumentModel>, ExchangeRateMixin<SaleAdvanceDocumentModel>, CommunityTransactionForSaleMixin<SaleAdvanceDocumentModel>, SaleLinksMixin<SaleAdvanceDocumentModel>, TaxLiabilityDateMixin<SaleAdvanceDocumentModel>, BankAccountNumberMixin<SaleAdvanceDocumentModel> { };

@BO({
    modelType: SaleAdvanceDocumentModel,
    mixins: [ExchangeRateMixin, CommunityTransactionForSaleMixin, SaleLinksMixin, TaxLiabilityDateMixin, BankAccountNumberMixin, ClientMixin]
})
export class SaleAdvanceBO extends BusinessObjectBase<SaleAdvanceDocumentModel> {
    public constructor(
        weaver: Weaver,
        protected readonly clientRepository: ClientRepositoryBase,
        protected readonly featureFlagsRulesService: FeatureFlagsRulesServiceBase,
        protected readonly queryBus: QueryBus,
        protected readonly productRepository: ProductRepositoryContract
    ) {
        super(weaver);
        this.initBOParts();
    }

    private initBOParts(): void {
        this.parts.push(...[
            new SaleDocumentItemsValidationsBoPart(this.weaver, this.productRepository, this.featureFlagsRulesService, this.queryBus),
            new DocumentVatRateForSaleValidationBoPart(this.weaver),
            new DocumentItemsNumberingBoPart(this.weaver),
            new SaleDocumentCalculatorBoPart(this.weaver),
            new SaleDocumentWithDueDateBoPart(this.weaver),
            new SaleDocumentPaymentsBoPart(this.weaver),
            new SaleDocumentClientBoPart(this.weaver, this.featureFlagsRulesService),
            new SaleDocumentSplitPaymentBoPart(this.weaver, this.productRepository, this.featureFlagsRulesService),
            new SaleDocumentReverseChargeAndNPBoPart(this.weaver),
            new DocumentNumberBoPart(this.weaver, this.queryBus),
            new DocumentWstoBoPart(this.weaver, this.queryBus),
            new SaleDocumentJpkGroupsBoPart(this.weaver, this.featureFlagsRulesService),
            new SaleDocumentVatReleaseBoPart(this.weaver, this.queryBus),
            new DocumentExchangeRateValidationBoPart(this.weaver),
            new SaleDocumentKsefBoPart(this.weaver, () => this.previousAdvanceModel),
            new SaleDocumentIssuedToInventoryGoodsIssueBoPart(this.weaver)
        ]);
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        await Promise.resolve([
            this.localContext.preload(CurrencyCollectionQuery),
            this.localContext.preload(SaleKindQuery),
            this.localContext.preload(CompanyParametersQuery),
            this.localContext.preload(VatRateForSaleQuery),
            this.localContext.preload(LoggedUserQuery),
            this.localContext.preload(UserCollectionQuery),
            this.localContext.preload(NumberingQuery),
            this.localContext.preload(VatReleaseReasonQuery),
            this.localContext.preload(ContextKeys.company),
            this.localContext.preload(ContextKeys.isInvoiceToInventoryGoodsIssue(this.model.sun), () => Promise.resolve(!!this.linksInventory)),
        ]);

        this.registerReadonlyRules();
        this.registerPropretyChanged();
        this.registerValidationRules();

        if (this.model.isPersistent) {
            await this.loadSourceClient();
            await this.loadLinksAsync();
            this.updateFirstDocumentProperties(this.links);
            await this.localContext.refresh(ContextKeys.isInvoiceToInventoryGoodsIssue(this.model.sun));

            if (this.lastAdvance && this.model.firstAdvanceDocumentId) {
                const previousAdvanceId = this.previousAdvanceId(this.links, this.model.id);
                await this.setPreviousAdvanceModelAsync(previousAdvanceId);
            }

            await this.updateAdvnaceValuesAsync();
            this.internalRemainingDocumentValue = this.calculateRemainingAdvance();
        }

        await this.setVatRateChangeUnavailableAsync();
    }

    private updateFirstDocumentProperties(links: DocumentLinks): void {
        this.model.firstAdvanceDocumentId = links.advances.filter(x => x.documentId < links.current.documentId).length > 0 ? links.advances[0].documentId : null as any;
        this.model.firstAdvanceDocumentVersion = links.advances.filter(x => x.documentId < links.current.documentId).length > 0 ? links.advances[0].version : null as any;
    }

    public get remainingAmountToPay(): number {
        const { precision } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.model.currencyId })!;
        const remaining = Calc.round(Math.abs(this.model.advanceValue) - this.paid, precision, MidpointRounding.AwayFromZero);
        return remaining;
    }

    public get paid(): number {
        if (this.model.currencyId === 0) {
            return 0;
        }

        const { precision } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.model.currencyId })!;
        return Calc.round(this.model.payments.reduce((sum, { value }) => sum + value, 0), precision, MidpointRounding.AwayFromZero);
    }

    private registerValidationRules(): void {
        this.registerAdvanceValueValidationRules();
        this.registerOnePaymentOnAdvanceRule();
        this.registerAmountOfPaymentsGreaterThanAdvanceValueRule();
        this.registerIssueDateEarlierThanPreviousAdvance();
        this.registerSaleDateValidationRules();
        this.registerTaxLiabilityDateValidationRules();
        this.registerVatRateOnItemValidationRules();
    }

    private registerPropretyChanged(): void {
        this.updateAdvanceValues();
        this.updateBankAccountNumberAfterPaymentMethodChange();
        this.registerPaymentDateRuleAfterPaymentDateChange();
        this.registerUpdatingRemainingDocumentValueHandler();
        this.registerRefreshExchangeRateHandler();
        this.applyDiscountToNewItemOnCollectionChanged();
    }

    private registerReadonlyRules(): void {
        this.readonly.addRule([
            [SaleAdvanceDocumentModel, 'client'],
            [SaleAdvanceDocumentModel, 'jpkVatGroups'],
            [SaleAdvanceDocumentModel, 'communityTransactionId'],
            [SaleAdvanceDocumentModel, 'currencyId'],
            [SaleAdvanceDocumentModel, 'vatReleaseReasonId'],
            [SaleAdvanceDocumentModel, 'vatReleaseReasonCustomName'],
            [SaleAdvanceDocumentModel, 'vatReleaseReasonSource'],
            [SaleAdvanceDocumentModel, 'calculationMethod'],
            [SaleAdvanceDocumentModel, 'rateNP'],
            [SaleAdvanceDocumentModel, 'issuedByVatPayer'],
            [SaleAdvanceDocumentModel, 'reverseCharge']],
            () => this.isNewAdvanceToAdvance || this.isEditAdvanceToAdvance || this.isEditFirstAdvanceInChain
        );

        this.readonly.addRule([
            [SaleAdvanceDocumentModel, 'communityTransactionId'],
            [SaleAdvanceDocumentModel, 'transactionLocation'],
            [SaleAdvanceDocumentModel, 'issueDate'],
            [SaleAdvanceDocumentModel, 'saleDate'],
            [SaleAdvanceDocumentModel, 'goodsIssueDate'],
            [SaleAdvanceDocumentModel, 'bankAccountNumberId'],
            [SaleAdvanceDocumentModel, 'issuer'],
            [SaleAdvanceDocumentModel, 'recipient'],
            [SaleAdvanceDocumentModel, 'client'],
            [SaleAdvanceDocumentModel, 'buyer'],
            [SaleAdvanceDocumentModel, 'advanceValue'],
            [SaleAdvanceDocumentModel, 'vatReleaseReasonId'],
            [SaleAdvanceDocumentModel, 'vatReleaseReasonCustomName'],
            [SaleAdvanceDocumentModel, 'vatReleaseReasonSource'],
            [SaleAdvanceDocumentModel, 'splitPayment'],
            [SaleAdvanceDocumentModel, 'paymentMethodId'],
            [SaleAdvanceDocumentModel, 'currencyId'],
            [SaleAdvanceDocumentModel, 'calculationMethod'],
            [SaleAdvanceDocumentModel, 'dueDate'],
            [SaleAdvanceDocumentModel, 'dueDateInDays'],
            [SaleAdvanceDocumentModel, 'jpkVatGroups'],
            [SaleAdvanceDocumentModel, 'customDueDateDescription'],
            [SaleAdvanceDocumentModel, 'wstoConsumerCountryId'],
            [SaleAdvanceDocumentModel, 'wstoShipmentCountryId'],
            [SaleAdvanceDocumentModel, 'issuedByVatPayer'],
            [SaleAdvanceDocumentModel, 'exchangeRate'],
            [SaleAdvanceDocumentModel, 'numberingId'],
            [SaleAdvanceDocumentModel, 'ksefSubmissionMode'],
            [SaleAdvanceDocumentModel, 'orderNumber'],
            [SaleAdvanceDocumentModel, 'orderDate'],
            [SaleAdvanceDocumentModel, 'agreementNumber'],
            [SaleAdvanceDocumentModel, 'agreementDate'],
            [SaleAdvanceDocumentModel, 'rateNP'],
            [SaleAdvanceDocumentModel, 'reverseCharge']],
            () => this.linksAdjusted || ((this.isEditAdvanceToAdvance || this.isEditFirstAdvanceInChain) && !this.lastAdvance)
        );

        this.readonly.setReadonlyOnSubtree(SaleAdvanceDocumentItemModel, () => this.isNewAdvanceToAdvance || this.isEditAdvanceToAdvance || this.linksAdjusted || this.isEditFirstAdvanceInChain, {
            exceptions: [
                [SaleAdvanceDocumentItemModel, <keyof SaleAdvanceDocumentItemModel>'note']
            ]
        });

        this.readonlyChecker.addOmitRule([
            [SaleAdvanceDocumentItemModel, 'vatRateId'],
            [SaleAdvanceDocumentItemModel, 'price_grossBeforeDiscount'],
            [SaleAdvanceDocumentItemModel, 'value_grossBeforeDiscount'],
            [SaleAdvanceDocumentItemModel, 'price_grossAfterDiscount'],
            [SaleAdvanceDocumentItemModel, 'value_grossAfterDiscount'],
            [SaleAdvanceDocumentItemModel, 'price_netBeforeDiscount'],
            [SaleAdvanceDocumentItemModel, 'value_netBeforeDiscount'],
            [SaleAdvanceDocumentItemModel, 'price_netAfterDiscount'],
            [SaleAdvanceDocumentItemModel, 'value_netAfterDiscount'],
        ], () => !this.isVatRateChangeUnavailable);

        this.readonlyChecker.addOmitRule([
            [SaleAdvanceDocumentModel, 'inventoryEffect']
        ]);

        this.readonly.setReadonlyOnSubtree(AddressModel, () => this.linksAdjusted);
        this.readonly.setReadonlyOnSubtree(ExchangeRateModel, () => {
            return !(this.isNewAdvanceToAdvance || this.lastAdvance) && this.model.isPersistent;
        });
    }

    protected calculateDateForCurrencyExchangeRate(): Date {
        const date = this.model.issuedByVatPayer
            ? getMinimalOfDates(this.model.issueDate, this.model.taxLiabilityDate)
            : this.model.saleDate ? dateDiffInDays(this.model.issueDate, this.model.saleDate) < 0 ? this.model.issueDate : this.model.saleDate : this.model.issueDate;

        return BusinessDayCalculatorPL.getLastBusinessDay(substractDays(date!, 1));
    }

    private _isVatRateChangeUnavailable = false;
    public get isVatRateChangeUnavailable() {
        return this._isVatRateChangeUnavailable;
    }

    private async setVatRateChangeUnavailableAsync(): Promise<void> {
        if (this.linksInventory) {
            this._isVatRateChangeUnavailable = true;
            return;
        }
        if (this.model.firstAdvanceDocumentId === null && !this.model.isPersistent) {
            this._isVatRateChangeUnavailable = false;
            return;
        }

        const companyParameters = this.localContext.getPreloaded(CompanyParametersQuery)!;
        const { id: systemCurrencyId } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: companyParameters.systemCurrency })!;

        const id = this.model.id === 0 ? this.model.firstAdvanceDocumentId! : this.model.id;
        const links = await this.localContext.getAsync(ContextKeys.documentLinks(id), () => this.queryBus.queryAsync(new DocumentLinksQuery(id)));
        const advanceNo = links.advances.findIndex(link => link.documentId === this.model.id);
        const hasFollowers = this.model.isPersistent && advanceNo < links.advances.length - 1;
        const isAdjustedDocument = (links.adjustments?.length ?? 0) > 0;

        const prevAdvance = links.advances[links.advances.findIndex(link => link.documentId === this.model.id) - 1] ?? last(links.advances);
        const prevAdvanceSummary = await this.localContext.getAsync(ContextKeys.advanceSummary(prevAdvance.documentId), () => this.queryBus.queryAsync(new AdvanceSummaryQuery(prevAdvance.documentId)));
        const vatRateChanged = vatRateChangedOnSaleAdvance(prevAdvanceSummary);

        const advanceInForeignCurrency = this.model.currencyId !== systemCurrencyId;

        // jeśli po tym dokumencie są inne to zablokowane lub jeśli jest to dokument po zmianie stawki lub wystawiono korektę łańucha
        // lub jeśli dokument w walucie obcej
        this._isVatRateChangeUnavailable = hasFollowers || vatRateChanged || isAdjustedDocument || advanceInForeignCurrency;
    }

    private registerAdvanceValueValidationRules(): void {
        this.validation.addRule(
            [[SaleAdvanceDocumentModel, 'advanceValue'], [SaleAdvanceDocumentModel, 'grossValue']],
            [[SaleAdvanceDocumentModel, 'advanceValue']],
            ctx => {
                return this.model.items.length
                    ? ctx.bo.remainingDocumentValue >= 0
                    : not(ctx.bo.model.advanceValue > 0);
            },
            AdvanceValueGreaterThanRemainingAmount
        );
        this.validation.addRule(
            [[SaleAdvanceDocumentModel, 'advanceValue']],
            ctx => {
                return !(ctx.source.advanceValue < 0);
            },
            AdvanceValueLessThanZeroError
        );
    }

    private registerAmountOfPaymentsGreaterThanAdvanceValueRule(): void {
        this.validation.addRule(
            [[SaleAdvanceDocumentModel, 'advanceValue']],
            [[DocumentPaymentListItemModel, 'value']],
            ctx => {
                return ctx.target === ctx.bo.model.payments[ctx.bo.model.payments.length - 1]
                    ? !(ctx.bo.paid > ctx.bo.model.advanceValue)
                    : true;
            },
            AmountOfPaymentGreaterThanAdvanceValueError
        );
    }

    private registerOnePaymentOnAdvanceRule(): void {
        this.validation.addRule(
            [[SaleAdvanceDocumentModel, 'advanceValue']],
            [[DocumentPaymentListItemModel, 'value']],
            ctx => {
                if (!ctx.bo.isFinalAdvance) {
                    return !(ctx.bo.model.payments.length > 1) && (ctx.bo.model.payments.reduce((sum, { value }) => sum + value, 0) === ctx.bo.model.advanceValue);
                }
                return true;
            },
            OnePaymentOnAdvanceDocumentError
        );
    }

    private registerIssueDateEarlierThanPreviousAdvance(): void {
        this.validation.addRule(
            [[SaleAdvanceDocumentModel, 'issueDate']],
            ctx => {
                if (this.previousAdvanceModel) {
                    const currentIssueDate = new Date(ctx.source.issueDate);
                    const previousIssueDate = new Date(this.previousAdvanceModel.issueDate);
                    return dateDiffInDays(currentIssueDate, previousIssueDate) >= 0;
                }
                return true;
            },
            IssueDateEarlierThanPreviousAdvanceError
        );
    }

    private registerSaleDateValidationRules(): void {
        this.validation.addRule(
            [[SaleAdvanceDocumentModel, 'saleDate']],
            ctx => {
                if (this.previousAdvanceModel && this.isFinalAdvance) {
                    const currentPaymentDate = new Date(ctx.source.saleDate!);
                    const previousPaymentDate = this.previousAdvanceModel.payments.length > 0 ? new Date(this.previousAdvanceModel.payments[0].date) : null;
                    return previousPaymentDate ? dateDiffInDays(currentPaymentDate, previousPaymentDate) >= 0 : true;
                }
                return true;
            },
            SaleDateEarlierThanPreviousAdvanceError
        );
    }

    private registerTaxLiabilityDateValidationRules(): void {
        this.validation.addRule(
            [[SaleAdvanceDocumentModel, 'taxLiabilityDate']],
            async ctx => {
                if (this.model.issuedByVatPayer && ctx.source.taxLiabilityDate !== null) {
                    return !(await this.isTaxLiabilityDateIncludedInAccountancyAsync());
                }
                return true;
            },
            DateIncludedInAccountingWarning
        );
    }

    private registerVatRateOnItemValidationRules(): void {
        this.validation.addRule(
            [[SaleAdvanceDocumentItemModel, 'vatRateId']],
            async ctx => {
                if (this.model.firstAdvanceDocumentId == null)
                    return true;

                const id = this.model.id === 0 ? this.model.firstAdvanceDocumentId : this.model.id;
                const links = await this.localContext.getAsync(ContextKeys.documentLinks(id), () => this.queryBus.queryAsync(new DocumentLinksQuery(id)));
                const prevAdvance = links.advances[links.advances.findIndex(link => link.documentId === this.model.id) - 1] ?? last(links.advances);
                const prevAdvanceSummary = await this.localContext.getAsync(ContextKeys.advanceSummary(prevAdvance.documentId), () => this.queryBus.queryAsync(new AdvanceSummaryQuery(prevAdvance.documentId)));
                const itemHistory = prevAdvanceSummary.itemsHistory[ctx.source.ordinalNumber];
                const vatRateChanged = vatRateChangedOnSaleAdvanceItem(itemHistory);
                const { rate: currentVatRate } = this.localContext.firstPreloaded(VatRateForSaleQuery, { id: ctx.source.vatRateId })!;

                // jeśli na poprzednim dokumencie była zmiana, to na tym jest niedozwolona
                return not(vatRateChanged && currentVatRate !== last(itemHistory).vatRate);
            },
            VatRateHasAlreadyChangedOnItemError
        );
    }

    private applyDiscountToNewItemOnCollectionChanged(): void {
        const reactToDocumentItemsCollectionChange = collectionChangedJoinPointFilter(this)(this.model.items);
        this.registerListening(this.weaver.register(CollectionChangedJoinPoint, reactToDocumentItemsCollectionChange(
            (ctx: any) => (ctx.newItems as any[]).forEach((item: SaleAdvanceDocumentItemModel) => this.applyClientDiscountToItem(item))
        )));
    }

    private _isNewAdvanceToAdvance!: boolean;
    public get isNewAdvanceToAdvance(): boolean {
        return this._isNewAdvanceToAdvance;
    }

    public get isEditAdvanceToAdvance(): boolean {
        return !this.isFirstAdvance && this.model.isPersistent;
    }

    public get isFinalAdvance(): boolean {
        return this.remainingDocumentValue === 0;
    }

    public get isFirstAdvance(): boolean {
        const advances = this.links?.advances || this.sourceAdvanceLinks?.advances;
        if (!advances) return true;
        const [first] = advances;
        return first.documentId === this.model.id;
    }

    public get isEditFirstAdvanceInChain(): boolean {
        if (!this.model.isPersistent || !this.links) return false;
        if (this.links.advances?.length <= 1) return false;
        const [first] = this.links.advances;

        return this.model.id === first.documentId;
    }

    public get lastAdvance(): boolean {
        if (!this.model.isPersistent) return false;
        return this.links?.advances.length > 0 && this.links.advances.filter(x => x.documentId > this.model.id).length === 0;
    }

    private internalRemainingDocumentValue = 0;
    public get remainingDocumentValue(): number {
        return this.internalRemainingDocumentValue;
    }

    private previousAdvanceId(links: DocumentLinks, currentId: number): number {
        const filtered = links.advances.filter(x => x.documentId < currentId);
        const last = filtered.pop();
        return last?.documentId ?? null as any;
    }

    private updateAdvanceValues(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(SaleAdvanceDocumentModel, ['firstAdvanceDocumentId'])(
            this.updateAdvnaceValuesAsync.bind(this)
        )));
    }

    private async updateAdvnaceValuesAsync(): Promise<void> {
        const links = this.model.isPersistent ? this.links : this.sourceAdvanceLinks;

        if (links) {
            if (this.isNewAdvanceToAdvance) {
                const advanceSummary = await this.queryBus.queryAsync(new AdvanceSummaryQuery(links.advances[links.advances.length - 1].documentId));
                this.model.vatSummary = advanceSummary;
            } else if (!this.isFirstAdvance) {
                const advanceSummary = await this.queryBus.queryAsync(new AdvanceSummaryQuery(this.previousAdvanceId(links, this.model.id)));
                this.model.vatSummary = advanceSummary;
            }
            this.internalRemainingDocumentValue = this.calculateRemainingAdvance();
        }
    }

    private registerUpdatingRemainingDocumentValueHandler(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(SaleAdvanceDocumentModel, ['advanceValue', 'netValue', 'grossValue'])(
            _ => {
                this.internalRemainingDocumentValue = this.calculateRemainingAdvance();
                this.model.saleDate = this.isFinalAdvance
                    ? this.model.saleDate || (this.model.payments.length ? new Date(this.model.payments[0].date) : new Date(this.model.issueDate))
                    : null as any;
            }
        )));
    }

    private registerRefreshExchangeRateHandler(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(SaleAdvanceDocumentModel, ['saleDate'])(
            ctx => {
                if (this.model.issuedByVatPayer || (!ctx.oldValue && !this.model.saleDate)) return;
                if (ctx.oldValue && this.model.saleDate && dateDiffInDays(ctx.oldValue, this.model.saleDate) === 0) return;
                if (this.isFinalAdvance) {
                    this.refreshExchangeRateAsync();
                }

            }
        )));
    }

    private calculateRemainingAdvance(): number {
        const { precision } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.model.currencyId })!;
        if (isUndefined(precision))
            return 0;

        const items = this.model.items.map(item => ({
            no: item.ordinalNumber,
            vatRateId: item.vatRateId,
            vatRate: this.localContext.firstPreloaded(VatRateForSaleQuery, { id: item.vatRateId })!.rate,
            netValue: item.value_netAfterDiscount,
            grossValue: item.value_grossAfterDiscount,
            vatValue: item.value_vatAfterDiscount
        }));

        const calculator = <AdvanceInvoiceDocumentGrossCalculator>getDocumentCalculatorForSaleDocumentKind(this.model.kind, this.model.calculationMethod, this.model.issuedByVatPayer);
        return calculator.calculateRemainingAdvance(items, {
            precision,
            advanceValue: this.model.advanceValue,
            vatSummary: <AdvanceSummary>this.model.vatSummary
        });
    }

    private registerPaymentDateRuleAfterPaymentDateChange(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(DocumentPaymentListItemModel, ['date'])(
            () => this.validation.queueValidation([SaleAdvanceDocumentModel, 'saleDate'])
        )));
    }

    private updateBankAccountNumberAfterPaymentMethodChange(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(SaleAdvanceDocumentModel, ['paymentMethodId'])(
            _ => {
                const config = this.localContext.firstPreloaded(SaleKindQuery, { name: this.model.kind })!;
                if (config.defaultPaymentMethodId === this.model.paymentMethodId) {
                    this.model.dueDateInDays = config.defaultDueDate;
                }
                this.updateBankAccountNumber();
            }
        )));
    }

    private previousAdvanceModel!: SaleAdvanceDocumentModel;
    private async setPreviousAdvanceModelAsync(id: number): Promise<void> {
        const plainObject = await this.queryBus.queryAsync(new SaleAdvancePlainObjectQuery(), id);
        this.previousAdvanceModel = rewriteAdvancePlainObjectToDocumentModel(plainObject);
    }

    private internalSourceAdvanceLinks!: DocumentLinks;
    public get sourceAdvanceLinks(): DocumentLinks {
        return this.internalSourceAdvanceLinks;
    }

    private async setSourceAdvanceLinks(requestedAdvanceId: number): Promise<number> {
        const requestedAdvanceLink = await this.localContext.getAsync(ContextKeys.documentLinks(requestedAdvanceId), () => this.queryBus.queryAsync(new DocumentLinksQuery(requestedAdvanceId)));
        const lastAdvanceLinkId = requestedAdvanceLink.advances[requestedAdvanceLink.advances.length - 1].documentId;
        if (requestedAdvanceId !== lastAdvanceLinkId) {
            this.internalSourceAdvanceLinks = await this.localContext.getAsync(ContextKeys.documentLinks(lastAdvanceLinkId), () => this.queryBus.queryAsync(new DocumentLinksQuery(lastAdvanceLinkId)));
        } else {
            this.internalSourceAdvanceLinks = requestedAdvanceLink;
        }

        return lastAdvanceLinkId;
    }

    public async fillAdvanceIssuedToAdvanceAsync(requestedAdvanceId: number): Promise<void> {
        this._isNewAdvanceToAdvance = true;

        const lastAdvanceId = await this.setSourceAdvanceLinks(requestedAdvanceId);
        await this.setPreviousAdvanceModelAsync(lastAdvanceId);

        const firstAdvanceLink = this.internalSourceAdvanceLinks.advances[0];

        this.model.firstAdvanceDocumentVersion = firstAdvanceLink ? firstAdvanceLink.version : this.previousAdvanceModel.version;
        this.model.firstAdvanceDocumentId = firstAdvanceLink ? firstAdvanceLink.documentId : this.previousAdvanceModel.id;

        this.model.advanceId = requestedAdvanceId;

        const now = Date.now();
        this.model.issueDate = new Date(now);
        this.model.saleDate = new Date(now);
        if (this.previousAdvanceModel.issuedByVatPayer) {
            this.model.taxLiabilityDate = new Date(now);
        }

        const { id: loggedUserId } = this.localContext.getPreloaded(LoggedUserQuery)!;
        const companyParameters = this.localContext.getPreloaded(CompanyParametersQuery)!;
        const vatRates = this.localContext.getPreloaded(VatRateForSaleQuery)!;
        const loggedUser = this.localContext.firstPreloaded(UserCollectionQuery, { id: loggedUserId })!;
        this.model.issuer = `${loggedUser.firstName} ${loggedUser.lastName}`;

        // rewrite
        this.model.calculationMethod = this.previousAdvanceModel.calculationMethod;
        this.model.communityTransactionId = this.previousAdvanceModel.communityTransactionId;
        this.model.currencyId = this.previousAdvanceModel.currencyId;
        this.model.transactionLocation = this.previousAdvanceModel.transactionLocation;
        this.model.note = this.previousAdvanceModel.note;

        this.model.issuedByVatPayer = this.previousAdvanceModel.issuedByVatPayer;
        rewriteVatReleaseProperties(this.model, this.previousAdvanceModel, { companyParameters, vatRates });

        this.model.cashBasis = this.previousAdvanceModel.cashBasis;
        this.model.numberingId = this.previousAdvanceModel.numberingId;
        this.model.splitPayment = this.previousAdvanceModel.splitPayment;
        this.model.jpkVatGroups = this.previousAdvanceModel.jpkVatGroups;
        this.model.invoiceToReceipt = this.previousAdvanceModel.invoiceToReceipt;

        //#region order must be kept
        this.model.paymentMethodId = this.previousAdvanceModel.paymentMethodId;
        this.model.dueDate = this.previousAdvanceModel.dueDate;
        this.model.dueDateInDays = this.previousAdvanceModel.dueDateInDays;
        //#endregion

        this.model.bankAccountNumberId = this.previousAdvanceModel.bankAccountNumberId;
        this.model.baseCurrencyBankAccountNumberId = this.previousAdvanceModel.baseCurrencyBankAccountNumberId;

        this.model.wstoAdditionalInformation = this.previousAdvanceModel.wstoAdditionalInformation;
        this.model.wstoConsumerCountryId = this.previousAdvanceModel.wstoConsumerCountryId;
        this.model.wstoShipmentCountryId = this.previousAdvanceModel.wstoShipmentCountryId;

        this.model.orderNumber = this.previousAdvanceModel.orderNumber;
        this.model.orderDate = this.previousAdvanceModel.orderDate;
        this.model.agreementNumber = this.previousAdvanceModel.agreementNumber;
        this.model.agreementDate = this.previousAdvanceModel.agreementDate;
        this.model.reverseCharge = this.previousAdvanceModel.reverseCharge;
        this.model.rateNP = this.previousAdvanceModel.rateNP;

        await rewriteDocumentContractorWithFreshDataAsync(this.model, this.previousAdvanceModel, this.clientRepository);

        if (this.previousAdvanceModel.deliveryAddress) {
            const deliveryAddress = new AddressModel();
            this.model.deliveryAddress = deliveryAddress;
            rewriteAddress(deliveryAddress, this.previousAdvanceModel.deliveryAddress);
        }

        this.previousAdvanceModel.items.map(item => {
            const model = new SaleAdvanceDocumentItemModel();
            this.model.items.push(model);
            rewriteSaleDocumentItem(model, item);
        });

        if (this.model.issuedByVatPayer) {
            this.recalculateTaxLiabilityDate();
        }

        await this.refreshExchangeRateAsync();
        await this.waitForProcessingCompletionAsync();
    }

    public async fillProFormaOrderAsync(source: SaleProFormaModel): Promise<void> {
        const companyParameters = this.localContext.getPreloaded(CompanyParametersQuery)!;
        const vatRates = this.localContext.getPreloaded(VatRateForSaleQuery)!;
        const communityTransactions = this.localContext.getPreloaded(CommunityTransactionForSaleQuery)!;
        const currencies = this.localContext.getPreloaded(CurrencyCollectionQuery)!;
        const currencyId = source.currencyId;
        const precision = currencies.find(item => item.id === currencyId)!.precision;
        const documentConfiguration = this.localContext.firstPreloaded(SaleKindQuery, { name: this.model.kind })!;
        const numberings = this.localContext.getPreloaded(NumberingQuery)!;

        await rewriteAdvanceInvoiceFromProFormaAsync(this.model, source, { companyParameters, communityTransactions, precision: precision, defaultCalculationMethod: null as any, vatRates }, this.clientRepository);

        this.model.payments.forEach(p => p.onDocument = true);

        if (companyParameters.vatPayer) {
            this.model.issuedByVatPayer = companyParameters.vatPayer;
        }
        if (documentConfiguration.useCustomTransactionLocation) {
            this.model.transactionLocation = documentConfiguration.customTransactionLocation;
        } else {
            const { city } = this.localContext.getPreloaded<Company>(ContextKeys.company)!;
            this.model.transactionLocation = city;
        }

        if (source.exchangeRate) {
            await this.refreshExchangeRateAsync();
        }

        const [{ id: defaultNumberingId }] = numberings.filter(n => n.default && n.kinds.indexOf(this.model.kind) > -1);
        this.model.numberingId = defaultNumberingId;

        this.model.splitPayment = source.splitPayment;

        this.model.dueDateInDays = documentConfiguration.defaultDueDate;

        this.model.implementedDocumentId = source.id;
        this.model.implementedDocumentVersion = source.version;

        this.model.currencyId = currencyId;

        this.model.bankAccountNumberId = source.bankAccountNumberId;
        this.model.baseCurrencyBankAccountNumberId = source.baseCurrencyBankAccountNumberId;

        const loggedUserQueryInfo = this.localContext.getPreloaded(LoggedUserQuery)!;
        const loggedUserId = loggedUserQueryInfo.id;
        const loggedUser = this.localContext.firstPreloaded(UserCollectionQuery, { id: loggedUserId })!;
        this.model.issuer = `${loggedUser.firstName} ${loggedUser.lastName}`;

        this.model.advanceValue = this.paid;
                
        const transaction = communityTransactions.find(elem => elem.id === source.communityTransactionId)!;
        const defaults = getDefaultNPValues(transaction.symbol);
        this.model.reverseCharge = defaults.reverseCharge;
        this.model.rateNP = defaults.rateNP;

        await this.waitForProcessingCompletionAsync();
    }

    public async refreshDocumentItemsPricesAsync(): Promise<void> {
        await Promise.all(this.model.items.map(async item => await refreshSaleDocumentItemPriceAsync(item, this, this.productRepository)));
    }

    public get previousCommunityTransactionId(): number {
        return this.previousAdvanceModel?.communityTransactionId;
    }
}
